import { useState, useEffect } from 'react';
import { fetchPicksData, transformPicksData } from '../services/apiService';

// Initialize empty picks structure for fallback
const initializeEmptyPicks = (participants) => {
  const emptyPicks = {};
  participants.forEach((participant) => {
    emptyPicks[participant] = {};
    for (let i = 16; i >= 1; i--) {
      emptyPicks[participant][i] = { pick: '', winner: null, locked: false, dns: false, dis: false, notes: '' };
    }
    emptyPicks[participant]['Tie'] = { pick: '', winner: null, locked: false, dns: false, dis: false, notes: '' };
    emptyPicks[participant]['Total'] = { status: 'calculated', notes: '' };
  });
  return emptyPicks;
};

export const usePicksData = (weekNumber, seasonYear, cachedParticipants, cachedUserIds, cachedUrlLinks) => {
  const [picks, setPicks] = useState({});
  const [participantFlags, setParticipantFlags] = useState({}); // Track dns/dis per participant
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0); // Trigger refetch
  const [gamesInWeek, setGamesInWeek] = useState(0); // Track number of games in the week

  // Function to manually trigger a refetch
  const refetchData = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  // Fetch picks data when week changes or refetch is triggered
  useEffect(() => {
    let active = true;
    const loadPicksData = async () => {
      if (!weekNumber || !seasonYear || !cachedParticipants || cachedParticipants.length === 0) return;
      
      setLoading(true);
      try {
        // Fetch picks for the week
        const rawPicksData = await fetchPicksData(weekNumber, seasonYear);
        if (!active) return;
        
        // Extract dns/dis flags from picks data
        const flags = {};
        if (rawPicksData && rawPicksData.weekly_picks && rawPicksData.weekly_picks.length > 0) {
          rawPicksData.weekly_picks.forEach((user) => {
            const displayName = user.username;
            flags[displayName] = {
              dns: user.dns,
              dis: user.dis
            };
          });
        }
        setParticipantFlags(flags);
        
        // Initialize empty structure for all participants
        const fullPicksData = initializeEmptyPicks(cachedParticipants);
        
        // Transform and merge API data if available
        let mergedData = fullPicksData;
        if (rawPicksData && rawPicksData.weekly_picks && rawPicksData.weekly_picks.length > 0) {
          const transformedData = transformPicksData(rawPicksData.weekly_picks);
          mergedData = { ...fullPicksData, ...transformedData };
        }
        
        setPicks(mergedData);
        setGamesInWeek(rawPicksData?.games_in_week || 0);
        setError(null);
      } catch (err) {
        if (!active) return;
        setParticipantFlags({});
        setError('Could not load picks data - showing empty form');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPicksData();
    return () => { active = false; };
  }, [weekNumber, seasonYear, cachedParticipants, refetchTrigger]);

  return {
    picks,
    setPicks,
    participantFlags,
    loading,
    error,
    refetchData,
    gamesInWeek
  };
};
